import { useState, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useSetores } from "@/hooks/use-setores";
import { type CorFita, type InsertMangara } from "@shared/schema";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { format } from "date-fns";

type MangaraLine = {
  id: string;
  setorId: string;
  quantidadeCachos: number;
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RegistrarMangaraDialog({ open, onOpenChange }: Props) {
  const { toast } = useToast();
  const [dataCorteMangara, setDataCorteMangara] = useState(format(new Date(), "yyyy-MM-dd"));
  const [corFitaId, setCorFitaId] = useState("");
  const [observacoes, setObservacoes] = useState("");
  const [lines, setLines] = useState<MangaraLine[]>([
    { id: crypto.randomUUID(), setorId: "", quantidadeCachos: 1 },
  ]);

  const { setores } = useSetores({ somenteProdutivos: true });

  const { data: cores } = useQuery<CorFita[]>({
    queryKey: ["/api/cores"],
  });

  // Apenas PRODUTIVOS, filtrados por ativo e ordenados
  const sortedSetores = [...(setores?.filter(s => s.ativo) || [])].sort((a, b) =>
    a.nome.localeCompare(b.nome)
  );

  // Cores already come sorted by ordem from the backend
  const sortedCores = cores?.filter(c => c.ativo) || [];

  // Calculate total mangarás
  const totalMangaras = lines.reduce((acc, line) => {
    return acc + (line.setorId ? line.quantidadeCachos : 0);
  }, 0);

  const userStr = localStorage.getItem("user");
  const user = userStr ? JSON.parse(userStr) : null;
  
  // Use ref to prevent double-submission (belt and suspenders)
  const isSubmittingRef = useRef(false);

  const createMangarasMutation = useMutation({
    mutationFn: async (data: { mangaras: InsertMangara[]; lineIds: string[] }) => {
      const { mangaras, lineIds } = data;
      const results: { mangara: any; lineId: string }[] = [];
      const errors: { lineId: string; error: string }[] = [];
      
      // Process sequentially to handle partial success properly
      for (let i = 0; i < mangaras.length; i++) {
        try {
          const response = await apiRequest("POST", "/api/mangaras", mangaras[i]);
          const result = await response.json();
          results.push({ mangara: result, lineId: lineIds[i] });
        } catch (error: any) {
          errors.push({ lineId: lineIds[i], error: error.message });
        }
      }
      
      return { results, errors };
    },
    onSuccess: ({ results, errors }) => {
      // Always invalidate queries since some data may have been saved
      queryClient.invalidateQueries({ queryKey: ["/api/mangaras"] });
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["/api/ciclos"] });
      queryClient.invalidateQueries({ queryKey: ["/api/previsao-semanal"] });
      
      // Remove successfully saved lines from the form
      if (results.length > 0) {
        const savedLineIds = new Set(results.map(r => r.lineId));
        setLines(prevLines => {
          const remainingLines = prevLines.filter(line => !savedLineIds.has(line.id));
          // Keep at least one empty line
          return remainingLines.length > 0 
            ? remainingLines 
            : [{ id: crypto.randomUUID(), setorId: "", quantidadeCachos: 1 }];
        });
      }
      
      if (errors.length === 0) {
        // Complete success
        toast({
          title: `${results.length} mangará(s) registrado(s)!`,
          description: "Os ciclos foram gerados automaticamente.",
        });
        handleReset();
        onOpenChange(false);
      } else if (results.length > 0) {
        // Partial success
        toast({
          title: `${results.length} mangará(s) registrado(s)`,
          description: `${errors.length} falharam. Verifique os itens restantes.`,
          variant: "destructive",
        });
      } else {
        // Complete failure
        toast({
          title: "Erro ao registrar mangarás",
          description: errors[0]?.error || "Erro desconhecido",
          variant: "destructive",
        });
      }
      
      isSubmittingRef.current = false;
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao registrar mangarás",
        description: error.message,
        variant: "destructive",
      });
      isSubmittingRef.current = false;
    },
  });

  const handleAddLine = () => {
    setLines([...lines, { id: crypto.randomUUID(), setorId: "", quantidadeCachos: 1 }]);
  };

  const handleRemoveLine = (id: string) => {
    if (lines.length > 1) {
      setLines(lines.filter((line) => line.id !== id));
    }
  };

  const handleLineChange = (id: string, field: keyof Omit<MangaraLine, "id">, value: string | number) => {
    setLines(lines.map((line) =>
      line.id === id ? { ...line, [field]: value } : line
    ));
  };

  const handleSubmit = () => {
    // Prevent double-submission with ref guard
    if (isSubmittingRef.current || createMangarasMutation.isPending) {
      return;
    }
    
    if (!dataCorteMangara || !corFitaId) {
      toast({
        title: "Campos obrigatórios",
        description: "Preencha a data do corte e a cor da fita.",
        variant: "destructive",
      });
      return;
    }

    const validLines = lines.filter((line) => line.setorId && line.quantidadeCachos > 0);
    if (validLines.length === 0) {
      toast({
        title: "Nenhum setor válido",
        description: "Adicione pelo menos um setor com quantidade.",
        variant: "destructive",
      });
      return;
    }

    const mangaras: InsertMangara[] = validLines.map((line) => ({
      setorId: line.setorId,
      corFitaId,
      dataCorteMangara,
      quantidadeCachos: line.quantidadeCachos,
      observacoes: observacoes || undefined,
      usuarioId: user?.id || "",
    }));
    
    const lineIds = validLines.map((line) => line.id);

    // Set guard before mutation
    isSubmittingRef.current = true;
    createMangarasMutation.mutate({ mangaras, lineIds });
  };

  const handleReset = () => {
    setDataCorteMangara(format(new Date(), "yyyy-MM-dd"));
    setCorFitaId("");
    setObservacoes("");
    setLines([{ id: crypto.randomUUID(), setorId: "", quantidadeCachos: 1 }]);
    isSubmittingRef.current = false;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-4 md:p-6">
        <DialogHeader>
          <DialogTitle className="text-xl md:text-2xl font-semibold">Registrar Corte de Mangará</DialogTitle>
          <DialogDescription className="text-xs md:text-sm">
            Selecione a data e cor da fita, depois adicione os setores e quantidades.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 md:space-y-6">
          {/* Data e Cor */}
          <div className="grid gap-4 md:gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="dataCorteMangara">Data do Corte *</Label>
              <Input
                id="dataCorteMangara"
                type="date"
                className="font-mono"
                value={dataCorteMangara}
                onChange={(e) => setDataCorteMangara(e.target.value)}
                data-testid="input-data-corte"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="corFitaId">Cor da Fita *</Label>
              <Select value={corFitaId} onValueChange={setCorFitaId}>
                <SelectTrigger data-testid="select-cor">
                  <SelectValue placeholder="Selecione a cor" />
                </SelectTrigger>
                <SelectContent>
                  {sortedCores.map((cor) => (
                    <SelectItem key={cor.id} value={cor.id}>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-4 h-4 rounded border"
                          style={{ backgroundColor: cor.hexColor }}
                        />
                        <span>{cor.nome}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Tabela de Setores */}
          <div className="space-y-3">
            <Label className="text-sm">Setores e Quantidades *</Label>

            <div className="border rounded-md overflow-x-auto">
              <Table className="text-xs md:text-sm">
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-32">Setor</TableHead>
                    <TableHead className="min-w-24">Variedade</TableHead>
                    <TableHead className="min-w-20">Qtd</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lines.map((line, index) => {
                    const selectedSetor = setores?.find((s) => s.id === line.setorId);
                    return (
                      <TableRow key={line.id}>
                        <TableCell className="p-2 md:p-3">
                          <Select
                            value={line.setorId}
                            onValueChange={(value) => handleLineChange(line.id, "setorId", value)}
                          >
                            <SelectTrigger className="h-8 text-xs" data-testid={`select-setor-${index}`}>
                              <SelectValue placeholder="Selecione" />
                            </SelectTrigger>
                            <SelectContent className="text-xs">
                              {sortedSetores.map((setor) => (
                                <SelectItem key={setor.id} value={setor.id} className="text-xs">
                                  {setor.nome}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="p-2 md:p-3 text-xs text-muted-foreground truncate">
                          {selectedSetor?.variedade?.nome || "-"}
                        </TableCell>
                        <TableCell className="p-2 md:p-3">
                          <Input
                            type="number"
                            min="1"
                            className="h-8 text-xs w-16"
                            value={line.quantidadeCachos}
                            onChange={(e) =>
                              handleLineChange(line.id, "quantidadeCachos", parseInt(e.target.value) || 1)
                            }
                            data-testid={`input-quantidade-${index}`}
                          />
                        </TableCell>
                        <TableCell className="p-2 md:p-3">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleRemoveLine(line.id)}
                            disabled={lines.length === 1}
                            data-testid={`button-remove-${index}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 md:gap-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="text-xs h-8"
                onClick={handleAddLine}
                data-testid="button-add-line"
              >
                <Plus className="h-3 w-3 mr-1" />
                Adicionar Setor
              </Button>
              
              <div className="text-xs md:text-sm font-medium whitespace-nowrap" data-testid="total-mangaras">
                Total: <span className="text-base md:text-lg font-bold">{totalMangaras}</span>
              </div>
            </div>
          </div>

          {/* Observações */}
          <div className="space-y-2">
            <Label htmlFor="observacoes" className="text-sm">Observações Gerais</Label>
            <Textarea
              id="observacoes"
              placeholder="Observações sobre o corte (aplicadas a todos os registros)..."
              className="text-sm min-h-20"
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              data-testid="input-observacoes"
            />
          </div>
        </div>

        <DialogFooter className="flex-col-reverse md:flex-row gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="button-cancel"
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={createMangarasMutation.isPending}
            data-testid="button-submit"
          >
            {createMangarasMutation.isPending ? "Salvando..." : "Registrar Todos"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
